<template>
  <div class="navbar_quick_action pt-[2px]">
    <CustomDropdown
      gap="10px"
      position="end"
      dropdownCSS="p-0"
      ref="dropdownRef"
    >
      <template #trigger>
        <a-tooltip placement="bottom">
          <template #title>
            <span class="text-[13px] font-normal primary_color"
              >Notifications</span
            >
          </template>

          <div class="notification_container relative cursor-pointer">
            <ThemeImage
              :icon="imageNames.NOTIFICATION"
              alt="notification_icon"
              class="size-5"
            />
            <div
              v-if="notificationCount > 0"
              class="notification_badge px-[3px] absolute top-0 right-0 text_white text-xs font-semibold rounded-full min-w-[14px] h-[14px] flex items-center justify-center transform translate-x-1/2 -translate-y-1/2"
            >
              {{ displayCount }}
            </div>
          </div>
        </a-tooltip>
      </template>

      <template #default>
        <div
          id="nav_dropdown_header"
          class="p-4 items-center border_bottom w-96"
        >
          <span class="text-base text_color font-semibold">Notifications</span>
        </div>
        <div
          v-if="notifications.length > 0"
          class="flex flex-col w-96 max-h-96 overflow-auto gap-2 p-2"
        >
          <div v-for="notification in notifications" :key="notification._id">
            <NotificationItem
              v-if="notification.type === notificationType.commentMentioned"
              :key="notification.id"
              :sender-name="notification.senderName"
              :entity-type="notification.entity_type"
              :entity-name="notification.entity_name"
              :content="notification.content"
              :created-time="notification.created_at"
              :is-read="notification.is_read"
              @click="handleOpenNotification(notification, 'comments')"
            />
            <MarketResearchNotification
              v-if="notification.type === 'research_completed'"
              :entity-type="notification.entity_type"
              :entity-name="notification.entity_name"
              :is-read="notification.is_read"
              :created-time="notification.created_at"
              @click="handleOpenNotification(notification, 'research')"
            />
            <EntityNotification
              v-if="isEntityNotification(notification.type)"
              :entity-type="notification.entity_type"
              :entity-name="notification.entity_name"
              :content="notification.content"
              :is-read="notification.is_read"
              :created-time="notification.created_at"
              @click="handleOpenNotification(notification, 'activity')"
            />
            <OutreachReplyCompletedNotification
              v-if="notification.type === 'outreach_reply'"
              :entity-type="notification.entity_type"
              :entity-name="notification.entity_name"
              :is-read="notification.is_read"
              :created-time="notification.created_at"
              @click="handleOpenNotification(notification, 'activity')"
            />

            <SequenceReplyCompletedNotification
              v-if="notification.type === 'sequence_reply'"
              :entity-type="notification.entity_type"
              :entity-name="notification.entity_name"
              :is-read="notification.is_read"
              :created-time="notification.created_at"
              @click="handleOpenNotification(notification, 'activity')"
            />

            <ExportCompletedNotification
              v-if="notification.type === notificationType.exportCompleted"
              :export-name="notification.export_name"
              :is-read="notification.is_read"
              :created-time="notification.created_at"
              @click="handleOpenNotification(notification)"
            />

            <EnrichmentNotification
              v-if="notification.type === 'enrichment_completed'"
              :entity-type="notification.entity_type"
              :entity-name="notification.entity_name"
              :entity-id="notification.entity_id"
              :download-url="notification.download_url"
              :file-name="notification.file_name"
              :is-read="notification.is_read"
              :created-time="notification.created_at"
              @click="handleOpenNotification(notification, 'enrichment')"
            />
          </div>
        </div>
        <div v-else class="flex flex-col gap-2 items-center py-20 h-96">
          <ThemeImage
            :icon="imageNames.NO_NOTIFICATIONS_GREY"
            alt="no_notifications"
          />
          <span class="proxima_nova text-base secondary_color"
            >No notifications found!</span
          >
        </div>
      </template>
    </CustomDropdown>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";

import CustomDropdown from "@/src/components/CustomDropdown.vue";
import NotificationItem from "@/src/components/notification_components/CommentMentionedNotification.vue";
import MarketResearchNotification from "@/src/components/notification_components/MarketResearchCompletedNotification.vue";
import DashboardService from "@/src/api/DashboardService.js";
import { getRouteFromEntity } from "@/src/utils/utils.js";
import mqtt from "mqtt";
import { message } from "ant-design-vue";
import { notificationType } from "@/src/constants/notificationTypeConstants.js";
import { useRouter } from "vue-router";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import { formatNotifications } from "@/src/utils/comment_notification_utils.js";

import eventBus from "@/src/utils/eventBus.js";
import OutreachReplyCompletedNotification from "./OutreachReplyCompletedNotification.vue";
import SequenceReplyCompletedNotification from "./SequenceReplyCompletedNotification.vue";
import ExportCompletedNotification from "@/src/components/notification_components/ExportCompletedNotification.vue";
import EnrichmentNotification from "@/src/components/notification_components/EnrichmentNotification.vue";
import EntityNotification from "@/src/components/notification_components/EntityNotification.vue";

import { imageNames } from "@/src/constants/imageConstants.js";

const notificationCount = ref(0);
const notifications = ref([]);
const dropdownRef = ref(null);

const router = useRouter();

const displayCount = computed(() => {
  return notificationCount.value > 99 ? "99+" : notificationCount.value;
});

const shouldUpdateCurrentPage = (currentRoute, entityType, entityId) => {
  const baseRoute = getRouteFromEntity(entityType);
  return (
    currentRoute.fullPath.startsWith("/" + baseRoute) &&
    currentRoute.params.id === entityId.toString()
  );
};

const isEntityNotification = (type) => {
  const entityNotificationTypes = [
    notificationType.emailClicked,
    notificationType.emailOpened,
    notificationType.taskAssigned,
    notificationType.linkedinInviteAccepted,
    notificationType.leadOwnerUpdate,
    notificationType.dealStatusUpdate,
    notificationType.dealOwnerUpdate,
    notificationType.meetingBooked,
    notificationType.contactOwnerUpdate,
  ];
  return entityNotificationTypes.includes(type);
};

const handleNotificationReceivedEvent = (message) => {
  fetchNotifications();
  const jsonString = message.toString("utf8");
  const data = JSON.parse(jsonString);
  if (data?.type === notificationType.exportCompleted) {
    return;
  }

  const { entity_type, entity_id } = data.payload;
  const currentRoute = router.currentRoute.value;
  if (shouldUpdateCurrentPage(currentRoute, entity_type, entity_id)) {
    eventBus.emit("refresh-" + entity_type + "-comments", entity_id);
  }
};

const handleOpenNotification = (notification, tab = "") => {
  if (!notification.is_read) {
    markAsRead(notification.id);
  }
  if (notification.type === notificationType.exportCompleted) {
    router.push({ path: "/settings", hash: "#exports" });
  } else {
    const baseRoute = getRouteFromEntity(notification.entity_type);
    if (baseRoute.length > 0 && notification.entity_id) {
      router.push(`/${baseRoute}/${notification.entity_id}?tab=${tab}`);
    }
  }
  if (dropdownRef.value && dropdownRef.value.closeDropdown) {
    dropdownRef.value.closeDropdown();
  }
  if (notification.type === notificationType.enrichmentCompleted) {
    router.push(`/data_enrichments?id=${notification.entity_id}`);
  }
};

const fetchNotifications = async () => {
  try {
    const response = await DashboardService.fetchNotifications();
    if (response.success) {
      notifications.value = formatNotifications(response.notifications);
      notificationCount.value = response.unread_count || 0;
    }
  } catch (error) {
    console.error("Error fetching notification: ", error);
    message.error("An error occurred while fetching the notification");
  }
};

const markAsRead = async (id) => {
  try {
    const response = await DashboardService.markNotificationAsRead([id]);
    if (response.success) {
      fetchNotifications().then();
    }
  } catch (error) {
    console.error("Error updating notification as read: ", error);
  }
};

onMounted(() => {
  fetchNotifications();
  eventBus.on("notification-received", handleNotificationReceivedEvent);
});

onUnmounted(() => {
  eventBus.off("notification-received", handleNotificationReceivedEvent);
});
</script>

<style scoped>
.notification_container {
  display: inline-block;
}

.notification_badge {
  background-color: rgba(250, 0, 0, 1);
}
</style>
